import {
  ActivitySpinnerSize, ActivitySpinnerWidget, TabActivationMode, TabAndPanelContents, TabListAlignment, TabStyles, TabsWidget, Tooltip
} from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { classes } from '~src/style';

import { SubspaceName } from '../../state/SpaceInfo';
import { SpaceContainerStyles } from '../../style/SpaceContainerStyles';

export interface TabbedSpaceProps {
  readonly subspaces: ReadonlyArray<Subspace>;
  readonly activeSubspaceName: SubspaceName;
  readonly urlForSubspace: (subspaceName?: SubspaceName) => string;
  readonly mastheadElement: React.ReactNode;
  readonly testIdPrefix: string;
  readonly spaceItemName: string;
}

export type Subspace = {
  readonly name: SubspaceName;
  readonly tabLabel: string;
  readonly renderContent: () => JSX.Element;
  readonly shouldRenderTabContentCount: boolean;
  readonly tabContentCount?: number;
  readonly tabContentCountUpToDate?: boolean;
  readonly tabTextOnHover?: string;
  readonly tabLowEmphesis?: boolean;
};

export const SpaceTabsTestId = 'space-tabs';
export const SpaceTabsContainerTestId = 'tabbed-space';
export const activeSubSpaceHeaderId = 'active-sub-space-header';

const HideTabPanelContent_WeManageItSeparately = <div style={{ height: '0' }}></div>;

export function spaceTabButtonContent(subspace: Subspace, isActiveTab: boolean): JSX.Element {
  return (
    <div
      className={classes(SpaceContainerStyles.SpaceTabContainer, subspace.tabLowEmphesis && !isActiveTab && SpaceContainerStyles.SpaceTabLowEmphesis)}
      style={{ fontWeight: isActiveTab ? 'bold' : 'normal' }}
    >
      <h2 id={isActiveTab ? activeSubSpaceHeaderId : null} className={SpaceContainerStyles.SpaceTabHeader}>{subspace.tabLabel}</h2>
      {subspace.shouldRenderTabContentCount &&
        <div className={SpaceContainerStyles.SpaceTabCountContainer} data-tb-test-id={'count-container'}>
          {subspace.tabContentCountUpToDate && subspace.tabContentCount}
          {!subspace.tabContentCountUpToDate &&
            <ActivitySpinnerWidget
              testId={'tab-waiting'}
              spinnerSize={ActivitySpinnerSize.Small} />
          }
        </div>
      }
    </div>
  );
}

function spaceTabButtonWithTooltip(subspace: Subspace, isActiveTab: boolean): JSX.Element {
  if (subspace.tabTextOnHover) {
    const tooltipContent = () => <div>{subspace.tabTextOnHover}</div>;
    return <Tooltip getContent={tooltipContent}>{spaceTabButtonContent(subspace, isActiveTab)}</Tooltip>;
  } else {
    return spaceTabButtonContent(subspace, isActiveTab);
  }
}

function getTabs(subspaces: ReadonlyArray<Subspace>, activeSubspaceName: string): TabAndPanelContents[] {
  return subspaces.map(subspace => ({
    panelContent: HideTabPanelContent_WeManageItSeparately,
    tabContent: {
      // temporary hack until UX/Widgets can agree on what tabs should look like
      label: undefined,
      icon: spaceTabButtonWithTooltip(subspace, activeSubspaceName === subspace.name),
      title: subspace.tabLabel
    }
  }));
}

export class TabbedSpace extends React.PureComponent<TabbedSpaceProps> {

  private memoizedGetTabs = defaultMemoize(getTabs);
  private tabsWidgetRef: TabsWidget;
  private tabsDimensionOverrides = {
    tabHeight: SpaceContainerStyles.SpaceTabHeight,
    tabHorizontalPadding: TabStyles.Spacing.LargeSpace
  };

  public componentDidMount() {
    // A space can mount with no tabs (e.g. an Admin Profile holder whose only tab is gated on
    // resource-actions that have not loaded yet). render() returns null in that case, so there is
    // no active tab to title -- skip until a tab exists (componentDidUpdate re-titles on change).
    if (this.props.subspaces.length === 0) {
      return;
    }
    const activeIndex = this.getActiveSubspaceIndex();
    this.setBrowserTitle(this.props.subspaces[activeIndex]);
  }

  private getActiveSubspaceIndex(): number {
    const index = this.props.subspaces.findIndex(subspace => subspace.name === this.props.activeSubspaceName);
    return index >= 0 ? index : 0;
  }

  private renderTabs(): JSX.Element {
    const tabs = this.memoizedGetTabs(this.props.subspaces, this.props.activeSubspaceName);
    return (
      <TabsWidget
        activationMode={TabActivationMode.Manual}
        anchorTabIndex={0}
        ariaLabel=''
        initialActiveIndex={this.getActiveSubspaceIndex()}
        onActiveIndexChanged={this.onTabIndexChanged}
        tabListAlignment={TabListAlignment.Left}
        tabs={tabs}
        testId={`${this.props.testIdPrefix}-${SpaceTabsTestId}`}
        ref={this.setTabsWidgetRef}
        dimensionOverrides={this.tabsDimensionOverrides}
      />
    );
  }

  private onTabIndexChanged = (index: number) => {
    const relevantSubspace = this.props.subspaces[index];
    const subspaceName = relevantSubspace.name;

    if (subspaceName !== this.props.activeSubspaceName) {
      const url = this.props.urlForSubspace(subspaceName);
      Location.instance.setUrl(url);
    }
  };

  private setBrowserTitle(subspace: Subspace) {
    BrowserTitleService.setTitleWithSpaceEntityNameAndTranslatedSubspaceName({
      spaceEntityName: this.props.spaceItemName,
      translatedSubspaceName: subspace.tabLabel,
      spaceType: this.props.testIdPrefix === 'schedule'
        ? Messages.schedules()
        : this.props.testIdPrefix === 'siteuser'
          ? Messages.users()
          : this.props.testIdPrefix === 'groupspace'
            ? Messages.groups()
            : undefined
    });
  }

  public render() {
    // TabsWidget cannot render an empty tab list -- renderTabPanel reads panelContent off a
    // nonexistent active tab and throws, which unmounts the space and blanks the page. Render
    // nothing until there is at least one tab; callers re-render with tabs once they resolve.
    if (this.props.subspaces.length === 0) {
      return null;
    }
    const activeSubspace = this.props.subspaces.find(s => s.name === this.props.activeSubspaceName);
    return (
      <div className={SpaceContainerStyles.SpaceContainer} role={'main'}>
        <div className={SpaceContainerStyles.MastheadArea}>
          {this.props.mastheadElement}
        </div>
        <div className={SpaceContainerStyles.SpaceTabsContainer} data-tb-test-id={SpaceTabsContainerTestId}>
          <div className={SpaceContainerStyles.SpaceTabsBottom} />
          {this.renderTabs()}
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <div className={SpaceContainerStyles.SubplaceContainer}>
            {activeSubspace ? activeSubspace.renderContent() : null}
          </div>
        </div>
      </div>
    );
  }

  public componentDidUpdate(prevProps: TabbedSpaceProps) {
    // if the active subspaces changes or the number of subspaces change, update the tabs widget
    if (this.tabsWidgetRef &&
      (prevProps.activeSubspaceName !== this.props.activeSubspaceName ||
        !_.isEqual(this.getTabNamesInOrder(prevProps.subspaces), this.getTabNamesInOrder(this.props.subspaces)))) {
      const activeIndex = this.getActiveSubspaceIndex();
      this.tabsWidgetRef.setActiveIndex(activeIndex);

      this.setBrowserTitle(this.props.subspaces[activeIndex]);
    }
  }

  private setTabsWidgetRef = (ref: TabsWidget) => {
    this.tabsWidgetRef = ref;
  };

  private getTabNamesInOrder(subspaces: readonly Subspace[]): SubspaceName[] {
    const names: SubspaceName[] = subspaces.map((subspace: Subspace) => subspace.name);
    return names;
  }
}
